import { Component } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ManagementArea } from 'src/app/data-models/area-models/management-area.model';
import { UserData } from 'src/app/data-models/user-models/user-data.model';
import { SubmissionService } from 'src/app/services/reporting/submission.service';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { PauseReport } from '../../data-models/report-models/pause-report.model';
import { Trip } from '../../data-models/trip-models/trip.model';
import { AuthenticationService } from '../../services/authentication.service';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-pause-report',
    templateUrl: './pause-report.component.html',
    styleUrls: ['./pause-report.component.css'],
    standalone: false
})
export class PauseReportComponent {

  isProduction: boolean = environment.production;

  isReportSending: boolean = false; // flag that tells us if the report is sending (so we can show the loading screen)

  userDetails: UserData = new UserData();
  currTrip: Trip = new Trip();
  pauseReport: PauseReport = new PauseReport();
  tripReportIndex!: number;
  managementAreas: ManagementArea[] = [];

  // data validation
  mgmtAreaValid: boolean = false;
  hasDuplicateDate: boolean = false; // a DFO-confirmed catch/pause report already exists for the chosen pause date

  reportStatus: string = '';
  isComplete: boolean = false;
  reportFailed: boolean = false;

  // report data
  pauseForm: UntypedFormGroup = new UntypedFormGroup({
    managementArea: new FormControl<string>('', [Validators.required]), // page 1
    licenseNum: new FormControl<string>(''),
    inactivityReason: new FormControl<string>(''),
    reportDtlComment: new FormControl<string>(''),
    comments: new FormControl<string>('') // optional 
  });

  formUpdateSubscription!: Subscription;

  pauseDate: Date = new Date(); // page 1
  pauseEndDate: Date = new Date();
  pauseEndTime: { hour: number, minute: number } = { hour: 0, minute: 0 };

  // page to page
  numPages: number = 0;
  currPage: number = 0;
  spinnerProgress: number = 0;
  spinnerIncrement: number = 0;


  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private submissionService: SubmissionService,
    private authenticationService: AuthenticationService,
    public sharedMethodsService: SharedMethodsService,
    public commonDataService: CommonDataService
  ) {

    // array of documents needed from the common data pouch for this component
    let requiredData: string[] = ['management_areas_list'];
    this.userDetails = UserStorageService.currUser;
    UserStorageService.getCurrTrip().then(() => {
      if (this.authenticationService.dataError(UserStorageService.error)) {
        return;
      }
      this.currTrip = UserStorageService.currTrip;
      this.tripReportIndex = this.currTrip.pauseFishingReports.findIndex((rep: PauseReport) => rep.internalId == this.route.snapshot.paramMap.get('id'));
      this.pauseReport = this.currTrip.pauseFishingReports[this.tripReportIndex];
      this.reportStatus = this.route.snapshot.queryParams.status;
      commonDataService.getSpecifiedDocs(this.userDetails.userEmail, requiredData).then(() => this.initialize());
    });
  }

  
  /*  ---------- Report Initialization Methods  ---------- */


  initialize(): void {

    if (this.reportStatus == 'new') {
      this.pauseEndTime = {minute: this.pauseEndDate.getMinutes(), hour: this.pauseEndDate.getHours()};
      this.pauseForm.get('licenseNum')?.setValue(this.userDetails.fin);
      // open on the first day of the trip still needing a report — FOS accepts one catch/pause report per day
      this.pauseDate = SharedMethodsService.getFirstUnreportedDay(this.currTrip);
    } else {
      this.patchForm();
    }

    this.checkDuplicateDate();

    this.formUpdateSubscription = this.pauseForm.valueChanges.subscribe(form => {
      this.pauseReport.managementArea = form.managementArea;
      this.pauseReport.licenseNumber = form.licenseNum;
      this.pauseReport.inactivityReason = form.inactivityReason;
      this.pauseReport.reportDTLNodeComments = form.reportDtlComment;
      this.pauseReport.comments = form.comments;
      this.pauseReport.lastUpdate = new Date();
      UserStorageService.saveTrip(this.currTrip, true, false);
    });
    
    this.managementAreas = CommonDataService.managementAreas;
    this.numPages = 3;
    this.spinnerIncrement = 100 / this.numPages;

    if (this.reportStatus == 'complete') {

      if (SharedMethodsService.getReportStatus(this.pauseReport.confirmationNumber) == 'Error with submission') {
        this.reportFailed = true;
      }
      this.isComplete = true;
      this.setSummaryPage();
      this.currPage = 4;
      this.spinnerProgress = 100;
    } else {
      this.toNextPage();
    }

  }


  patchForm() {
    this.pauseForm.patchValue({
      managementArea: this.pauseReport.managementArea,
      licenseNum: this.pauseReport.licenseNumber,
      inactivityReason: this.pauseReport.inactivityReason,
      reportDtlComment: this.pauseReport.reportDTLNodeComments,
      comments: this.pauseReport.comments,
    });

    this.pauseDate = this.pauseReport.pauseDate;
    this.pauseEndDate = this.pauseReport.pauseEndDate;
    this.pauseEndTime = {hour: this.pauseReport.pauseEndDate.getHours(), minute: this.pauseReport.pauseEndDate.getMinutes()};
  }


  // Reopens the report, only possible if a submitted report gets a failure response from FOS
  reopenReport() {
    this.currPage = 1;
    this.spinnerProgress = this.spinnerIncrement;
    this.isComplete = false;
    this.reportFailed = false;
  }


  dateTimeChange() {
    this.checkDuplicateDate();
    this.pauseReport.pauseDate = this.pauseDate;
    this.pauseReport.pauseEndDate = this.pauseEndDate;
    this.pauseReport.pauseEndDate.setHours(this.pauseEndTime.hour);
    this.pauseReport.pauseEndDate.setMinutes(this.pauseEndTime.minute);
    this.pauseReport.lastUpdate = new Date();
    UserStorageService.saveTrip(this.currTrip, true, false);
  }


  compareArea(a1: ManagementArea, a2: ManagementArea): boolean {
    return a1 && a2 ? a1.name == a2.name && a1.ID === a2.ID : a1 === a2;
  }


  // FOS accepts one catch/pause report per day and errors if either type already exists for the date,
  // so flag a pause date that collides with a report DFO has already confirmed
  checkDuplicateDate(): void {

    this.hasDuplicateDate = SharedMethodsService.hasConfirmedReportOnDate(this.currTrip, this.pauseDate, this.pauseReport.internalId);

  }

  /* ---------- Page Navigation Methods ---------- */


  toNextPage(): void {

    if (this.currPage > this.numPages) {
      // page is final page
      this.submitReport();
      return;
    }

    // leaving the pause date page: warn (but don't block) when DFO already confirmed a report for this date
    if (this.currPage === 1 && this.hasDuplicateDate) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: { screen: 'duplicate_report_date' }
      }).afterClosed().subscribe(result => {
        if (result) this.incrementPage();
      });
      return;
    }

    if (this.currPage === 3) {
      this.setSummaryPage();
    }

    this.incrementPage();

  }


  // go forward a page, changing the current page number and the spinner progress
  incrementPage(): void {

    this.currPage++;
    this.spinnerProgress += this.spinnerIncrement;

  }

  // check current page before going to the previous one
  toPreviousPage(): void {
    if (this.isComplete || this.currPage === 1) {
      this.sharedMethodsService.toHome(this.userDetails.currHomepage);
    } else {
      this.currPage--;
      this.spinnerProgress -= this.spinnerIncrement;  
    }   
  }


  // Navigate to the respective page based on the field that is required
  navigateToPage(page: number) {

    switch (page) {
      case 1:
        this.currPage = 1;
        break;
      case 2:
        this.currPage = 2;
        break;
      case 3:
        this.currPage = 3;
        break;
      default:
        break;
    }
  }


  // set all of the data to display on the summary based on the entered report fields
  setSummaryPage(): void {
    this.mgmtAreaValid = !this.pauseForm.get('managementArea')?.errors?.required;
  }


  /* ---------- Report Completion Methods ---------- */


   // verifies that all required report fields are complete, then initiates the saving/sending process
   submitReport(): void {

    if (this.pauseForm.valid) {
      this.isReportSending = true; // set flag
      this.saveReportData(); // save report data 
      this.attemptTransmission(); // attempt to send the report to DFO
    }
    else {
      this.sharedMethodsService.toastInvalidForm();
    }
  }

  // saves report data using the form data collected
  saveReportData(): void { 

    // save relevant information gathered in the form
    this.pauseReport.pauseDate = this.pauseDate;
    this.pauseReport.pauseDate.setHours(12, 0, 0, 0); // Set time to noon since the time isn't part of the submitted date
    this.pauseReport.managementArea = ManagementArea.transformAnonymous(this.pauseForm.get('managementArea')?.value);
    this.pauseReport.completionDate = new Date(); // get current date/time
    this.pauseReport.submissionDate = new Date();
    this.pauseReport.comments = this.pauseForm.get('comments')?.value.trim();
    this.pauseReport.isComplete = true;

  }

  
  // composes the email body and subject, then invokes a method to email the report to DFO
  attemptTransmission(): void {
    // A reopened report still carries the old FOS error text; clear it before the pre-send save,
    // otherwise the saveTrip merge sees matching completionDates and restores the stale error
    // over the resubmission (ties keep the error).
    if (SharedMethodsService.getReportStatus(this.pauseReport.confirmationNumber) === 'Error with submission')
      this.pauseReport.confirmationNumber = 'Awaiting response';
    UserStorageService.saveTrip(this.currTrip).then(() => {
      // change false to !isProduction to enable mocking the submission
      if (false) { // mock the submission process if the app is in development mode
        this.sharedMethodsService.toastEmailSent();
        this.pauseReport.confirmationNumber = "FOS 12345";
        this.pauseReport.isSubmitted = true;
        UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Pause Fishing Report', success: this.pauseReport.isSubmitted }, replaceUrl: true }));
      } else {
        this.submissionService.sendReportData(this.userDetails.userEmail, this.pauseReport.reportID, this.currTrip.tripID).then((success: boolean) => {
          if (success) { // if the email was sent successfully
            this.sharedMethodsService.toastEmailSent();
            this.pauseReport.isSubmitted = true; // set the report submissionflag
            this.pauseReport.confirmationNumber = "Awaiting response";
            UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Pause Fishing Report', success: this.pauseReport.isSubmitted }, replaceUrl: true }));
          } else {
            this.pauseReport.confirmationNumber = "Not submitted";
            this.sharedMethodsService.toastUnableToSubmitReport();
            this.currTrip.unsubmittedReports.push(this.pauseReport.completionDate);
            UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Pause Fishing Report', success: this.pauseReport.isSubmitted }, replaceUrl: true }));
          }
        });
      }
    });

  }


  deleteReport() {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {screen: 'delete_report'}
    }).afterClosed().subscribe(result => {
      if (result) {
        UserStorageService.deleteTripReport((trip) => trip.pauseFishingReports, this.pauseReport.internalId)
          .then(() => this.sharedMethodsService.toHome(this.userDetails.currHomepage));
      }     
    });
  }



  ngOnDestroy() {
    if (this.formUpdateSubscription) this.formUpdateSubscription.unsubscribe();
  }


}
